import React from 'react';
import { Heading, IconButton, Text, XIcon, Chip } from '@frontend/design-system';
import type { SeederInstance } from '../../types/sales-demo.types';
import type { DagNodeData } from '../../utils/dag-layout';

interface NodeDetailPanelProps {
  nodeData: DagNodeData | null;
  isOpen: boolean;
  onClose: () => void;
}

const seederStatusColors: Record<string, string> = {
  HEALTHY: '#4caf50',
  UNHEALTHY: '#f44336',
  DRAINING: '#9e9e9e',
};

const seederStatusVariant: Record<string, 'success' | 'critical' | 'neutral'> = {
  HEALTHY: 'success',
  UNHEALTHY: 'critical',
  DRAINING: 'neutral',
};

function formatHeartbeatAge(heartbeatTime: string): string {
  if (!heartbeatTime) return 'unknown';
  const diff = Date.now() - new Date(heartbeatTime).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
}

const SeederCard: React.FC<{ seeder: SeederInstance }> = ({ seeder }) => {
  const statusColor = seederStatusColors[seeder.status] ?? '#f44336';
  return (
    <div
      style={{
        border: `1px solid ${statusColor}40`,
        borderLeft: `3px solid ${statusColor}`,
        borderRadius: '6px',
        padding: '10px 12px',
        backgroundColor: seeder.status === 'HEALTHY' ? '#f9fdf9' : '#fff8f8',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text weight='bold' size='small'>
          {seeder.seederName}
        </Text>
        <Chip variant={seederStatusVariant[seeder.status] ?? 'critical'} size='tiny'>
          {seeder.status}
        </Chip>
      </div>
      <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>{seeder.seederAddress}</div>
      <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
        Heartbeat: {formatHeartbeatAge(seeder.heartbeatTime)}
        {seeder.ephemeralId ? ` · Ephemeral: ${seeder.ephemeralId}` : ''}
      </div>
      <div style={{ fontSize: '10px', color: '#aaa', marginTop: '2px', fontFamily: 'monospace' }}>
        {seeder.registrationId}
      </div>
    </div>
  );
};

export const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({ nodeData, isOpen, onClose }) => {
  if (!isOpen || !nodeData) return null;

  const unhealthySeeders = nodeData.seeders.filter((s) => s.status !== 'HEALTHY');
  const healthySeeders = nodeData.seeders.filter((s) => s.status === 'HEALTHY');

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: '420px',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: '-4px 0 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        padding: '20px',
        overflowY: 'auto',
        transform: 'translateX(0)',
        transition: 'transform 0.3s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Heading level={3}>{nodeData.label}</Heading>
        <IconButton label='Close' onClick={onClose}>
          <XIcon />
        </IconButton>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Text weight='bold' size='small' color='light'>
          Type URL
        </Text>
        <div
          style={{
            marginTop: '4px',
            fontSize: '11px',
            fontFamily: 'monospace',
            color: '#555',
            backgroundColor: '#f5f5f5',
            padding: '6px 8px',
            borderRadius: '4px',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {nodeData.typeUrl}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <div
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#e8f5e9',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#2e7d32' }}>{nodeData.healthyCount}</div>
          <div style={{ fontSize: '11px', color: '#4caf50' }}>Healthy</div>
        </div>
        <div
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#c62828' }}>{nodeData.unhealthyCount}</div>
          <div style={{ fontSize: '11px', color: '#f44336' }}>Unhealthy</div>
        </div>
        <div
          style={{
            flex: 1,
            padding: '10px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '20px', fontWeight: 700, color: '#616161' }}>{nodeData.totalCount}</div>
          <div style={{ fontSize: '11px', color: '#9e9e9e' }}>Total</div>
        </div>
      </div>

      {nodeData.hasUnhealthyUpstream && (
        <div
          style={{
            marginBottom: '16px',
            padding: '10px',
            backgroundColor: '#fff3e0',
            border: '1px solid #ff9800',
            borderRadius: '6px',
          }}
        >
          <Text weight='bold' size='small'>
            ⚠ Unhealthy Upstream
          </Text>
          <div style={{ fontSize: '12px', marginTop: '4px' }}>
            This dataset has upstream dependencies that are unhealthy.
          </div>
        </div>
      )}

      {unhealthySeeders.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <Text weight='bold' size='small' style={{ color: '#c62828', marginBottom: '8px', display: 'block' }}>
            Unhealthy Seeders ({unhealthySeeders.length})
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {unhealthySeeders.map((s) => (
              <SeederCard key={s.registrationId} seeder={s} />
            ))}
          </div>
        </div>
      )}

      {healthySeeders.length > 0 && (
        <div>
          <Text weight='bold' size='small' style={{ color: '#2e7d32', marginBottom: '8px', display: 'block' }}>
            Healthy Seeders ({healthySeeders.length})
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {healthySeeders.map((s) => (
              <SeederCard key={s.registrationId} seeder={s} />
            ))}
          </div>
        </div>
      )}

      {nodeData.totalCount === 0 && (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          <Text size='small'>No seeders registered for this dataset type.</Text>
        </div>
      )}
    </div>
  );
};
